<!--
  Comments on a span of code in the diff (logic/spanNote). Selecting text
  on one side's lines offers "Comment": it opens the usual comment on the
  lines the selection covers (`oncomment`), starting with the span quoted
  for GitHub and the marker that brings it back here. The offer sits where
  the pointer let go (keys: at the end they moved), with a small mark at
  the selection's other end that offers the same (logic/offerPlace). Each thread whose
  first comment carries a marker has its span tinted (CSS custom
  highlights: the diff's DOM is left as it is), where its quote still is
  (locateSpan); the thread keeps its place under its lines. Clicking a
  tinted span shows its thread (`onopen`). The tints follow the diff as it
  is drawn again.
-->
<script lang="ts" module>
  import type { Side } from '../logic/spanNote';
  /** A thread shown on lines here: its side, the lines it is on in this view, and its first comment's body and author. */
  export interface SpanThread {
    id: string;
    side: Side;
    from: number;
    to: number;
    body: string;
    author: string;
  }
</script>

<script lang="ts">
  import { locateSpan, parseSpanNote, spanFor, type SpanMarker } from '../logic/spanNote';
  import { pointAt, rangesFor } from '../logic/spanDom';
  import { offerPlaces, type Caret, type Point } from '../logic/offerPlace';

  interface Props {
    /** The diff as drawn (the reader's pane). */
    root: HTMLElement | null;
    /** Line `n` of `side` as the diff shown has it, or undefined past its end. */
    lineText: (side: Side, n: number) => string | undefined;
    threads: readonly SpanThread[];
    /** Whether a comment can be started (not read-only). */
    canComment: boolean;
    oncomment: (span: SpanMarker) => void;
    onopen: (id: string) => void;
  }
  let { root, lineText, threads, canComment, oncomment, onopen }: Props = $props();

  /** Bumped whenever the diff is drawn again, so the spans are placed again. */
  let drawn = $state(0);
  $effect(() => {
    const el = root;
    if (!el) return;
    let frame = 0;
    const mo = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        drawn++;
      });
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
    };
  });

  /** Each span thread's span as drawn: its ranges (none for one whose quote is gone, or whose lines are folded). */
  const placed = $derived.by(() => {
    void drawn;
    const el = root;
    if (!el) return [];
    return threads.flatMap((t) => {
      const m = parseSpanNote(t.body);
      if (!m || m.side !== t.side) return [];
      const at = locateSpan(m, (n) => lineText(t.side, n), { from: t.from, to: t.to });
      const ranges = at ? rangesFor(el, t.side, at.start, at.end) : [];
      return ranges.length ? [{ id: t.id, author: t.author, ranges }] : [];
    });
  });

  const HIGHLIGHT = 'span-note';
  const highlights = () => (typeof CSS !== 'undefined' && 'highlights' in CSS && typeof Highlight !== 'undefined' ? CSS.highlights : null);
  $effect(() => {
    const h = highlights();
    if (!h) return;
    h.set(HIGHLIGHT, new Highlight(...placed.flatMap((p) => p.ranges)));
    return () => h.delete(HIGHLIGHT);
  });

  /** The span thread whose tint is under viewport point (x, y). */
  function spanAt(x: number, y: number) {
    if (!placed.length) return null;
    const pos = document.caretPositionFromPoint?.(x, y);
    const caret = pos ? null : document.caretRangeFromPoint?.(x, y);
    const node = pos?.offsetNode ?? caret?.startContainer;
    const offset = pos?.offset ?? caret?.startOffset ?? 0;
    if (!node) return null;
    // The last one written: the one drawn on top.
    return [...placed].reverse().find((p) => p.ranges.some((r) => r.isPointInRange(node, offset))) ?? null;
  }
  $effect(() => {
    const el = root;
    if (!el) return;
    const click = (e: MouseEvent) => {
      if (e.button !== 0 || !document.getSelection()?.isCollapsed || (e.target as Element).closest('a, button, input, textarea, .gutter')) return;
      const p = spanAt(e.clientX, e.clientY);
      if (p) onopen(p.id);
    };
    let hovered: HTMLElement | null = null;
    const move = (e: PointerEvent) => {
      const p = spanAt(e.clientX, e.clientY);
      const code = p ? (e.target as Element).closest<HTMLElement>('.source-cell > code') : null;
      if (hovered && hovered !== code) {
        hovered.style.cursor = '';
        hovered.title = '';
      }
      hovered = code;
      if (code && p) {
        code.style.cursor = 'pointer';
        code.title = `${p.author} commented on this text: click to see it`;
      }
    };
    el.addEventListener('click', click);
    el.addEventListener('pointermove', move);
    return () => {
      el.removeEventListener('click', click);
      el.removeEventListener('pointermove', move);
      if (hovered) {
        hovered.style.cursor = '';
        hovered.title = '';
      }
    };
  });

  // ---------------------------------------------------------------- starting one

  /**
   * The selection's span, and where to offer "Comment": the button where
   * the pointer let go (or at the end keys moved), and a mark at the other
   * end, which also starts it (logic/offerPlace).
   */
  let chosen: { span: SpanMarker; button: { left: number; top: number }; mark: Point | null } | null = $state(null);
  /** Where the pointer last let go, while the selection is the pointer's (a key since makes it the keys'). */
  let released: Point | null = null;
  /** The button's size as placed (its text is fixed): used before it is drawn. */
  const BUTTON = { w: 90, h: 28 };

  /** The span of the text selected in the diff, if it is one: both ends on one side's lines. */
  function selectedSpan(): SpanMarker | null {
    const sel = document.getSelection();
    if (!root || !sel || sel.isCollapsed || !sel.rangeCount) return null;
    const r = sel.getRangeAt(0);
    if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return null;
    const a = pointAt(r.startContainer, r.startOffset);
    const b = pointAt(r.endContainer, r.endOffset);
    if (!a || !b || a.side !== b.side) return null;
    return spanFor(a.side, { line: a.line, col: a.col }, { line: b.line, col: b.col }, (n) => lineText(a.side, n));
  }
  /** The span selected now, for a key that comments on what is selected. */
  export function current(): SpanMarker | null {
    return canComment ? selectedSpan() : null;
  }

  /** The box of a caret at (`node`, `offset`); `fallback` where the browser gives a collapsed range none. */
  function caretAt(node: Node, offset: number, fallback: DOMRect): Caret {
    const r = document.createRange();
    r.setStart(node, offset);
    r.collapse(true);
    const box = r.getClientRects()[0] ?? r.getBoundingClientRect();
    return box && (box.top || box.bottom || box.left) ? { left: box.left, top: box.top, bottom: box.bottom } : { left: fallback.left, top: fallback.top, bottom: fallback.bottom };
  }
  function readSelection() {
    const span = canComment ? selectedSpan() : null;
    if (!span) return (chosen = null);
    const sel = document.getSelection()!;
    const r = sel.getRangeAt(0);
    const rects = r.getClientRects();
    const first = rects[0] ?? r.getBoundingClientRect();
    const last = rects[rects.length - 1] ?? first;
    // The selection's ends as the person made them: where it started (anchor) and where it moved to (focus).
    const forward = !!sel.anchorNode && sel.anchorNode === r.startContainer && sel.anchorOffset === r.startOffset;
    const anchor = sel.anchorNode ? caretAt(sel.anchorNode, sel.anchorOffset, forward ? first : last) : first;
    const focus = sel.focusNode ? caretAt(sel.focusNode, sel.focusOffset, forward ? last : first) : last;
    const placed = offerPlaces({ pointer: released, focus, anchor, viewport: { w: innerWidth, h: innerHeight }, button: BUTTON });
    chosen = { span, ...placed };
  }
  $effect(() => {
    let frame = 0;
    const changed = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(readSelection);
    };
    const scrolled = () => (chosen = null);
    const up = (e: PointerEvent) => {
      released = { x: e.clientX, y: e.clientY };
      changed();
    };
    // A selection moved with keys is placed by its end, not where the pointer last was.
    const key = () => (released = null);
    document.addEventListener('selectionchange', changed);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('keydown', key, true);
    addEventListener('scroll', scrolled, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('selectionchange', changed);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('keydown', key, true);
      removeEventListener('scroll', scrolled, true);
    };
  });

  function start() {
    if (!chosen) return;
    const span = chosen.span;
    chosen = null;
    document.getSelection()?.removeAllRanges();
    oncomment(span);
  }

</script>

{#if chosen}
  <button
    type="button"
    class="btn small span-note-offer"
    style:left="{chosen.button.left}px"
    style:top="{chosen.button.top}px"
    title="Comment on the selected text"
    onmousedown={(e) => e.preventDefault()}
    onclick={start}>Comment</button
  >
  {#if chosen.mark}
    <!-- The same offer at the selection's other end, for whoever looks for it there; the button above is the one in the tab order. -->
    <button
      type="button"
      class="span-note-mark"
      tabindex="-1"
      aria-label="Comment on the selected text"
      title="Comment on the selected text"
      style:left="{chosen.mark.x}px"
      style:top="{chosen.mark.y}px"
      onmousedown={(e) => e.preventDefault()}
      onclick={start}
    ></button>
  {/if}
{/if}

<style>
  /* The tint must read on added and removed lines' own backgrounds alike. */
  :global(::highlight(span-note)) {
    background-color: color-mix(in srgb, var(--accent) 32%, transparent);
    text-decoration: underline 2px var(--accent);
  }
  .span-note-offer {
    position: fixed;
    z-index: 60;
    box-shadow: 0 6px 18px #0000002e;
  }
  .span-note-mark {
    position: fixed;
    z-index: 60;
    width: 12px;
    height: 12px;
    margin: -6px 0 0 -6px;
    padding: 0;
    border: 2px solid var(--surface);
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 1px 4px #0004;
    cursor: pointer;
  }
</style>
